import {
  FileImage,
  Grid2X2,
  ScanLine,
  Sparkles,
  Upload,
  Zap
} from "lucide-react";
import React from "react";

export function FloorplanImportSetup({
  file,
  disabled,
  onFile,
  high,
  onHigh,
  regions,
  onRegions,
  wallsOnly,
  onWallsOnly,
  fast,
  onFast,
  children
}: {
  file?: File;
  disabled: boolean;
  onFile: (file: File) => void;
  high: boolean;
  onHigh: (value: boolean) => void;
  regions: boolean;
  onRegions: (value: boolean) => void;
  wallsOnly: boolean;
  onWallsOnly: (value: boolean) => void;
  fast: boolean;
  onFast: (value: boolean) => void;
  children?: React.ReactNode;
}) {
  const input = React.useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = React.useState(false);
  const options = [
    {
      label: "High detail",
      description: "More expensive and slower.",
      checked: high,
      change: onHigh,
      Icon: Sparkles
    },
    {
      label: "Process in regions",
      description: fast
        ? "Included in fast import."
        : "Faster, but less accurate.",
      checked: regions || fast,
      change: onRegions,
      Icon: Grid2X2,
      isLocked: fast
    },
    {
      label: "Walls only",
      description:
        "Include walls, doors and windows. Leave out furniture and desks.",
      checked: wallsOnly,
      change: onWallsOnly,
      Icon: ScanLine
    },
    {
      label: "Fast import (experimental)",
      description:
        "Smaller parts in parallel. Watch the map draw itself, and keep working if you close this window.",
      checked: fast,
      change: onFast,
      Icon: Zap
    }
  ];
  return (
    <div className="w-full space-y-5">
      <button
        type="button"
        disabled={disabled}
        onClick={() => input.current?.click()}
        onDragOver={(event) => {
          event.preventDefault();
          if (!disabled) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          if (!disabled && event.dataTransfer.files[0])
            onFile(event.dataTransfer.files[0]);
        }}
        className={`group flex w-full flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed px-6 py-8 text-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:cursor-wait disabled:opacity-60 ${dragging ? "border-blue-500 bg-blue-50" : "border-neutral-200 bg-neutral-50/60 hover:border-blue-400 hover:bg-blue-50/40"}`}
      >
        <span className="flex h-12 w-12 items-center justify-center rounded-xl border border-neutral-200 bg-white text-neutral-600 shadow-sm">
          {file ? <FileImage size={23} /> : <Upload size={23} />}
        </span>
        <span className="max-w-full break-all font-medium text-neutral-900">
          {file ? file.name : "Drop your floorplan here"}
        </span>
        <span className="text-neutral-500 text-sm">
          {file
            ? `${(file.size / 1024 / 1024).toFixed(1)} MB · Click to choose a different file`
            : "or click to browse your files"}
        </span>
        {!file && (
          <span className="rounded-lg bg-neutral-900 px-4 py-2 font-medium text-sm text-white">
            Choose floorplan
          </span>
        )}
        <span className="text-neutral-400 text-xs">
          PNG, JPG, WebP, SVG or PDF · up to 20 MB
        </span>
      </button>
      <input
        ref={input}
        aria-label="Floorplan image"
        type="file"
        accept=".png,.jpg,.jpeg,.webp,.svg,.pdf"
        disabled={disabled}
        className="hidden"
        onChange={(event) => {
          const next = event.target.files?.[0];
          event.target.value = "";
          if (next) onFile(next);
        }}
      />
      {children}
      <fieldset disabled={disabled}>
        <legend className="mb-3 font-medium text-neutral-900 text-sm">
          Make it your map
        </legend>
        <div className="grid gap-3 md:grid-cols-2">
          {options.map(
            ({ label, description, checked, change, Icon, isLocked }) => (
              <label
                key={label}
                className={`flex cursor-pointer flex-col gap-3 rounded-xl border p-4 transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-blue-500 ${checked ? "border-blue-300 bg-blue-50/60" : "border-neutral-200 bg-white hover:border-neutral-300"} ${disabled ? "cursor-wait opacity-60" : ""}`}
              >
                <span className="flex items-center justify-between">
                  <Icon
                    size={19}
                    className={checked ? "text-blue-600" : "text-neutral-400"}
                  />
                  <input
                    aria-label={label}
                    type="checkbox"
                    checked={checked}
                    disabled={isLocked}
                    onChange={(e) => change(e.target.checked)}
                    className="h-4 w-4 rounded accent-blue-600"
                  />
                </span>
                <span className="space-y-1">
                  <span className="block font-medium text-neutral-900 text-sm">
                    {label}
                  </span>
                  <span className="block text-neutral-500 text-xs leading-relaxed">
                    {description}
                  </span>
                </span>
              </label>
            )
          )}
        </div>
      </fieldset>
    </div>
  );
}
